import type { VisitTypeId } from '../services/api/types.gen';
import type { DisciplineType } from '../services/api/models/DisciplineType';
import type { OrderTypeId } from '../services/api/models/OrderTypeId';

/** Either a visit or an order task type. */
export type TaskTypeId = VisitTypeId | OrderTypeId;

let visitTypeLegacyById: Record<string, boolean> | null = null;

/** Populated when ``/visits/type-options`` is fetched (see useVisitTypeOptions). */
export function cacheVisitTypeLegacyFlags(
  options: ReadonlyArray<{ id: string; is_legacy: boolean }>
): void {
  visitTypeLegacyById = Object.fromEntries(
    options.map(option => [option.id, option.is_legacy])
  );
}

/**
 * Visit types shown in pickers only when `versioned-form-schemas` is enabled.
 * The OASIS form uses the pinned schema loader for these only with that flag;
 * without it, opening the visit would follow the legacy SOC schema path.
 */
export const VISIT_TYPES_REQUIRING_VERSIONED_FORM_SCHEMAS =
  new Set<VisitTypeId>([
    'OASIS_E2_START_OF_CARE_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_ENHANCED',
    'OASIS_E2_START_OF_CARE_PT_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_PT_ENHANCED',
    'OASIS_E2_START_OF_CARE_OT_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_OT_ENHANCED',
    'OASIS_E2_START_OF_CARE_ST_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_ST_ENHANCED',
    'OASIS_E2_RECERTIFICATION_ENHANCED',
    'OASIS_E2_RECERTIFICATION_PT_ENHANCED',
    'OASIS_E2_RECERTIFICATION_OT_ENHANCED',
    'OASIS_E2_RECERTIFICATION_ST_ENHANCED',
    'OASIS_E2_FOLLOW_UP_ENHANCED',
    'OASIS_E2_FOLLOW_UP_PT_ENHANCED',
    'OASIS_E2_FOLLOW_UP_OT_ENHANCED',
    'OASIS_E2_FOLLOW_UP_ST_ENHANCED',
    'OASIS_E2_TRANSFER_ENHANCED',
    'OASIS_E2_TRANSFER_PT_ENHANCED',
    'OASIS_E2_TRANSFER_OT_ENHANCED',
    'OASIS_E2_TRANSFER_ST_ENHANCED',
    'OASIS_E2_DISCHARGE_ENHANCED',
    'OASIS_E2_DISCHARGE_NON_BILLABLE',
    'OASIS_E2_DISCHARGE_PT_ENHANCED',
    'OASIS_E2_DISCHARGE_PT_NON_BILLABLE',
    'OASIS_E2_DISCHARGE_OT_ENHANCED',
    'OASIS_E2_DISCHARGE_OT_NON_BILLABLE',
    'OASIS_E2_DISCHARGE_ST_ENHANCED',
    'OASIS_E2_DISCHARGE_ST_NON_BILLABLE',
    'OASIS_E2_DEATH_ENHANCED',
    'OASIS_E2_DEATH_PT_ENHANCED',
    'OASIS_E2_DEATH_OT_ENHANCED',
    'OASIS_E2_DEATH_ST_ENHANCED',
  ]);

export const TASK_TYPE_DISCIPLINE_MAP: Record<TaskTypeId, DisciplineType> = {
  OASIS_E2_START_OF_CARE: 'SN',
  OASIS_E2_START_OF_CARE_ENHANCED: 'SN',
  OASIS_E2_RESUMPTION_OF_CARE: 'SN',
  OASIS_E2_RESUMPTION_OF_CARE_ENHANCED: 'SN',
  OASIS_E2_RECERTIFICATION: 'SN',
  OASIS_E2_RECERTIFICATION_ENHANCED: 'SN',
  OASIS_E2_DISCHARGE: 'SN',
  OASIS_E2_DISCHARGE_ENHANCED: 'SN',
  OASIS_E2_DISCHARGE_NON_BILLABLE: 'SN',
  OASIS_E2_START_OF_CARE_PT: 'PT',
  OASIS_E2_START_OF_CARE_PT_ENHANCED: 'PT',
  OASIS_E2_RESUMPTION_OF_CARE_PT: 'PT',
  OASIS_E2_RESUMPTION_OF_CARE_PT_ENHANCED: 'PT',
  OASIS_E2_RECERTIFICATION_PT: 'PT',
  OASIS_E2_RECERTIFICATION_PT_ENHANCED: 'PT',
  OASIS_E2_DISCHARGE_PT: 'PT',
  OASIS_E2_DISCHARGE_PT_ENHANCED: 'PT',
  OASIS_E2_DISCHARGE_PT_NON_BILLABLE: 'PT',
  OASIS_E2_START_OF_CARE_OT: 'OT',
  OASIS_E2_START_OF_CARE_OT_ENHANCED: 'OT',
  OASIS_E2_RESUMPTION_OF_CARE_OT: 'OT',
  OASIS_E2_RESUMPTION_OF_CARE_OT_ENHANCED: 'OT',
  OASIS_E2_RECERTIFICATION_OT: 'OT',
  OASIS_E2_RECERTIFICATION_OT_ENHANCED: 'OT',
  OASIS_E2_DISCHARGE_OT: 'OT',
  OASIS_E2_DISCHARGE_OT_ENHANCED: 'OT',
  OASIS_E2_DISCHARGE_OT_NON_BILLABLE: 'OT',
  OASIS_E2_START_OF_CARE_ST: 'ST',
  OASIS_E2_START_OF_CARE_ST_ENHANCED: 'ST',
  OASIS_E2_RESUMPTION_OF_CARE_ST: 'ST',
  OASIS_E2_RESUMPTION_OF_CARE_ST_ENHANCED: 'ST',
  OASIS_E2_RECERTIFICATION_ST: 'ST',
  OASIS_E2_RECERTIFICATION_ST_ENHANCED: 'ST',
  OASIS_E2_DISCHARGE_ST: 'ST',
  OASIS_E2_DISCHARGE_ST_ENHANCED: 'ST',
  OASIS_E2_DISCHARGE_ST_NON_BILLABLE: 'ST',
  OASIS_E2_FOLLOW_UP: 'SN',
  OASIS_E2_FOLLOW_UP_ENHANCED: 'SN',
  OASIS_E2_TRANSFER: 'SN',
  OASIS_E2_TRANSFER_ENHANCED: 'SN',
  OASIS_E2_DEATH: 'SN',
  OASIS_E2_DEATH_ENHANCED: 'SN',
  OASIS_E2_FOLLOW_UP_PT: 'PT',
  OASIS_E2_FOLLOW_UP_PT_ENHANCED: 'PT',
  OASIS_E2_TRANSFER_PT: 'PT',
  OASIS_E2_TRANSFER_PT_ENHANCED: 'PT',
  OASIS_E2_DEATH_PT: 'PT',
  OASIS_E2_DEATH_PT_ENHANCED: 'PT',
  OASIS_E2_FOLLOW_UP_OT: 'OT',
  OASIS_E2_FOLLOW_UP_OT_ENHANCED: 'OT',
  OASIS_E2_TRANSFER_OT: 'OT',
  OASIS_E2_TRANSFER_OT_ENHANCED: 'OT',
  OASIS_E2_DEATH_OT: 'OT',
  OASIS_E2_DEATH_OT_ENHANCED: 'OT',
  OASIS_E2_FOLLOW_UP_ST: 'ST',
  OASIS_E2_FOLLOW_UP_ST_ENHANCED: 'ST',
  OASIS_E2_TRANSFER_ST: 'ST',
  OASIS_E2_TRANSFER_ST_ENHANCED: 'ST',
  OASIS_E2_DEATH_ST: 'ST',
  OASIS_E2_DEATH_ST_ENHANCED: 'ST',
  SKILLED_NURSING_ASSESSMENT: 'SN',
  SKILLED_NURSING_EVALUATION: 'SN',
  LPN_LVN_SKILLED_NURSING_VISIT: 'SN',
  RN_SKILLED_NURSING_VISIT: 'SN',
  // Additional SNV aliases (all RN note form, SN discipline)
  SKILLED_NURSING_VISIT: 'SN',
  PRN_SKILLED_NURSING_VISIT: 'SN',
  WOUND_CARE_VISIT: 'SN',
  INFUSION_THERAPY_VISIT: 'SN',
  CATHETER_CARE_VISIT: 'SN',
  OSTOMY_CARE_VISIT: 'SN',
  DIABETIC_MANAGEMENT_VISIT: 'SN',
  MEDICATION_MANAGEMENT_VISIT: 'SN',
  INJECTION_VISIT: 'SN',
  LPN_LVN_WOUND_CARE_VISIT: 'SN',
  PICC_LABS: 'SN',
  SN_LABS: 'SN',
  FOLEY_CATHETER_CHANGE: 'SN',
  LVN_SUPERVISORY_VISIT: 'SN',
  PTA_SUPERVISORY_VISIT: 'PT',
  COTA_SUPERVISORY_VISIT: 'OT',
  HHA_SUPERVISORY_VISIT: 'HHA',
  AIDE_SUPERVISORY_VISIT: 'HHA',
  PT_EVALUATION: 'PT',
  PT_RE_EVALUATION: 'PT',
  PT_VISIT: 'PT',
  PT_TREATMENT_VISIT: 'PT',
  PT_DISCHARGE: 'PT',
  PT_DISCHARGE_VISIT: 'PT',
  OT_EVALUATION: 'OT',
  OT_RE_EVALUATION: 'OT',
  OT_VISIT: 'OT',
  OT_TREATMENT_VISIT: 'OT',
  OT_DISCHARGE: 'OT',
  OT_DISCHARGE_VISIT: 'OT',
  ST_EVALUATION: 'ST',
  ST_RE_EVALUATION: 'ST',
  ST_VISIT: 'ST',
  ST_TREATMENT_VISIT: 'ST',
  ST_DISCHARGE: 'ST',
  ST_DISCHARGE_VISIT: 'ST',
  MSW_EVALUATION: 'MSW',
  MSW_VISIT: 'MSW',
  MSW_DISCHARGE: 'MSW',
  AIDE_CARE_PLAN: 'HHA',
  AIDE_CARE_PLAN_REVISION: 'HHA',
  HHA_VISIT: 'HHA',
  // Order types (live in OrderTypeId after ROG-2320; values mirror
  // ORDER_TYPE_INFO[*].discipline in the backend — all SN today).
  CMS_485: 'SN',
  FREQUENCY_DISCONTINUE_ORDER: 'SN',
  FREQUENCY_ORDER: 'SN',
  PRN_ORDER: 'SN',
  CMS_486: 'SN',
  PHYSICIAN_ORDER: 'SN',
  POST_HOSPITAL_ORDER: 'SN',
  TP_1: 'SN',
  TP_2: 'SN',
  SCIC_ORDER: 'SN',
  F2F_ENCOUNTER: 'SN',
  MEDICAID_POC: 'SN',
  PLAN_OF_CARE_ADDENDUM: 'SN',
  MEDICATION_ORDER: 'SN',
};

// Wound and goal capability lists. These mirror the canonical backend sets in
// `backend/app/models/enum/visit.py` (`WOUND_ELIGIBLE_FORM_TYPES`,
// `GOAL_CREATION_ELIGIBLE_FORM_TYPES`, `GOAL_PROGRESS_ELIGIBLE_FORM_TYPES`)
// expanded from form types to visit type ids. They encode which sections
// WellSky/Kinnser actually offers on each note, so a clinician is never asked to
// chart something the upstream EMR cannot accept. Edit them alongside the
// backend sets: `backend/tests/models/test_visit_type_capabilities.py` fails if
// the two drift.

/**
 * Visit types with a wound section. Excludes speech therapy and death visits,
 * therapy transfers, non-OASIS OT notes, and supervisory, MSW, and aide visits.
 * The nursing transfer note does take wounds.
 */
const WOUND_ELIGIBLE_VISIT_TYPES: ReadonlySet<VisitTypeId> =
  new Set<VisitTypeId>([
    // SN
    'OASIS_E2_START_OF_CARE',
    'OASIS_E2_START_OF_CARE_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE',
    'OASIS_E2_RESUMPTION_OF_CARE_ENHANCED',
    'OASIS_E2_RECERTIFICATION',
    'OASIS_E2_RECERTIFICATION_ENHANCED',
    'OASIS_E2_DISCHARGE',
    'OASIS_E2_DISCHARGE_ENHANCED',
    'OASIS_E2_DISCHARGE_NON_BILLABLE',
    'OASIS_E2_FOLLOW_UP',
    'OASIS_E2_FOLLOW_UP_ENHANCED',
    'OASIS_E2_TRANSFER',
    'OASIS_E2_TRANSFER_ENHANCED',
    'SKILLED_NURSING_ASSESSMENT',
    'SKILLED_NURSING_EVALUATION',
    'LPN_LVN_SKILLED_NURSING_VISIT',
    'RN_SKILLED_NURSING_VISIT',
    'SKILLED_NURSING_VISIT',
    'PRN_SKILLED_NURSING_VISIT',
    'WOUND_CARE_VISIT',
    'INFUSION_THERAPY_VISIT',
    'CATHETER_CARE_VISIT',
    'OSTOMY_CARE_VISIT',
    'DIABETIC_MANAGEMENT_VISIT',
    'MEDICATION_MANAGEMENT_VISIT',
    'INJECTION_VISIT',
    'LPN_LVN_WOUND_CARE_VISIT',
    'PICC_LABS',
    'SN_LABS',
    'FOLEY_CATHETER_CHANGE',
    // PT
    'OASIS_E2_START_OF_CARE_PT',
    'OASIS_E2_START_OF_CARE_PT_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_PT',
    'OASIS_E2_RESUMPTION_OF_CARE_PT_ENHANCED',
    'OASIS_E2_RECERTIFICATION_PT',
    'OASIS_E2_RECERTIFICATION_PT_ENHANCED',
    'OASIS_E2_DISCHARGE_PT',
    'OASIS_E2_DISCHARGE_PT_ENHANCED',
    'OASIS_E2_DISCHARGE_PT_NON_BILLABLE',
    'OASIS_E2_FOLLOW_UP_PT',
    'OASIS_E2_FOLLOW_UP_PT_ENHANCED',
    'PT_EVALUATION',
    'PT_RE_EVALUATION',
    'PT_VISIT',
    'PT_TREATMENT_VISIT',
    'PT_DISCHARGE',
    'PT_DISCHARGE_VISIT',
    // OT
    'OASIS_E2_START_OF_CARE_OT',
    'OASIS_E2_START_OF_CARE_OT_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_OT',
    'OASIS_E2_RESUMPTION_OF_CARE_OT_ENHANCED',
    'OASIS_E2_RECERTIFICATION_OT',
    'OASIS_E2_RECERTIFICATION_OT_ENHANCED',
    'OASIS_E2_DISCHARGE_OT',
    'OASIS_E2_DISCHARGE_OT_ENHANCED',
    'OASIS_E2_DISCHARGE_OT_NON_BILLABLE',
    'OASIS_E2_FOLLOW_UP_OT',
    'OASIS_E2_FOLLOW_UP_OT_ENHANCED',
  ]);

export const supportsWounds = (visitType: VisitTypeId): boolean =>
  WOUND_ELIGIBLE_VISIT_TYPES.has(visitType);

/** Visit types that support goal addition (SOC and EVAL across all disciplines). */
const GOAL_CREATION_ELIGIBLE_VISIT_TYPES: ReadonlySet<VisitTypeId> =
  new Set<VisitTypeId>([
    // SOC
    'OASIS_E2_START_OF_CARE',
    'OASIS_E2_START_OF_CARE_ENHANCED',
    'OASIS_E2_START_OF_CARE_PT',
    'OASIS_E2_START_OF_CARE_PT_ENHANCED',
    'OASIS_E2_START_OF_CARE_OT',
    'OASIS_E2_START_OF_CARE_OT_ENHANCED',
    'OASIS_E2_START_OF_CARE_ST',
    'OASIS_E2_START_OF_CARE_ST_ENHANCED',
    // EVAL
    'SKILLED_NURSING_ASSESSMENT',
    'SKILLED_NURSING_EVALUATION',
    'PT_EVALUATION',
    'PT_RE_EVALUATION',
    'OT_EVALUATION',
    'OT_RE_EVALUATION',
    'ST_EVALUATION',
    'ST_RE_EVALUATION',
  ]);

export const canAddGoals = (visitType: VisitTypeId): boolean =>
  GOAL_CREATION_ELIGIBLE_VISIT_TYPES.has(visitType);

/**
 * Visit types that record goal and intervention progress: the routine notes,
 * matching what RogerDoc generates progress for. Kinnser itself permits progress
 * on more note types; widening both systems together is left for later.
 *
 * Disjoint from the creation list, so neither set alone answers whether a visit
 * shows a Goals section. Use `showsGoalsSection`.
 */
const GOAL_PROGRESS_ELIGIBLE_VISIT_TYPES: ReadonlySet<VisitTypeId> =
  new Set<VisitTypeId>([
    // SN (every visit type on the RN note)
    'RN_SKILLED_NURSING_VISIT',
    'SKILLED_NURSING_VISIT',
    'LPN_LVN_SKILLED_NURSING_VISIT',
    'PRN_SKILLED_NURSING_VISIT',
    'WOUND_CARE_VISIT',
    'LPN_LVN_WOUND_CARE_VISIT',
    'INFUSION_THERAPY_VISIT',
    'CATHETER_CARE_VISIT',
    'FOLEY_CATHETER_CHANGE',
    'OSTOMY_CARE_VISIT',
    'DIABETIC_MANAGEMENT_VISIT',
    'MEDICATION_MANAGEMENT_VISIT',
    'INJECTION_VISIT',
    'PICC_LABS',
    'SN_LABS',
    // PT
    'PT_VISIT',
    'PT_TREATMENT_VISIT',
    // OT
    'OT_VISIT',
    'OT_TREATMENT_VISIT',
    // ST
    'ST_VISIT',
    'ST_TREATMENT_VISIT',
  ]);

export const canTrackGoalProgress = (visitType: VisitTypeId): boolean =>
  GOAL_PROGRESS_ELIGIBLE_VISIT_TYPES.has(visitType);

/**
 * Whether the visit shows a Goals section at all. Creating goals and recording
 * progress happen on different notes, so the section needs either capability.
 * Mirrors `shows_goals_section` in the backend.
 */
export const showsGoalsSection = (visitType: VisitTypeId): boolean =>
  canAddGoals(visitType) || canTrackGoalProgress(visitType);

/** OASIS visit types that show the Active Diagnosis sidebar page (excludes DC and TF). */
const ACTIVE_DIAGNOSIS_ELIGIBLE_VISIT_TYPES: ReadonlySet<VisitTypeId> =
  new Set<VisitTypeId>([
    // SOC
    'OASIS_E2_START_OF_CARE',
    'OASIS_E2_START_OF_CARE_ENHANCED',
    'OASIS_E2_START_OF_CARE_PT',
    'OASIS_E2_START_OF_CARE_PT_ENHANCED',
    'OASIS_E2_START_OF_CARE_OT',
    'OASIS_E2_START_OF_CARE_OT_ENHANCED',
    'OASIS_E2_START_OF_CARE_ST',
    'OASIS_E2_START_OF_CARE_ST_ENHANCED',
    // ROC
    'OASIS_E2_RESUMPTION_OF_CARE',
    'OASIS_E2_RESUMPTION_OF_CARE_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_PT',
    'OASIS_E2_RESUMPTION_OF_CARE_PT_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_OT',
    'OASIS_E2_RESUMPTION_OF_CARE_OT_ENHANCED',
    'OASIS_E2_RESUMPTION_OF_CARE_ST',
    'OASIS_E2_RESUMPTION_OF_CARE_ST_ENHANCED',
    // REC
    'OASIS_E2_RECERTIFICATION',
    'OASIS_E2_RECERTIFICATION_ENHANCED',
    'OASIS_E2_RECERTIFICATION_PT',
    'OASIS_E2_RECERTIFICATION_PT_ENHANCED',
    'OASIS_E2_RECERTIFICATION_OT',
    'OASIS_E2_RECERTIFICATION_OT_ENHANCED',
    'OASIS_E2_RECERTIFICATION_ST',
    'OASIS_E2_RECERTIFICATION_ST_ENHANCED',
    // FUP
    'OASIS_E2_FOLLOW_UP',
    'OASIS_E2_FOLLOW_UP_ENHANCED',
    'OASIS_E2_FOLLOW_UP_PT',
    'OASIS_E2_FOLLOW_UP_PT_ENHANCED',
    'OASIS_E2_FOLLOW_UP_OT',
    'OASIS_E2_FOLLOW_UP_OT_ENHANCED',
    'OASIS_E2_FOLLOW_UP_ST',
    'OASIS_E2_FOLLOW_UP_ST_ENHANCED',
  ]);

export const supportsActiveDiagnosisPage = (visitType: VisitTypeId): boolean =>
  ACTIVE_DIAGNOSIS_ELIGIBLE_VISIT_TYPES.has(visitType);

export const isSNDiscipline = (typeId: TaskTypeId): boolean =>
  TASK_TYPE_DISCIPLINE_MAP[typeId] === 'SN';

/** Order type IDs. Mirrors backend `OrderTypeId` (app/models/enum/order.py). */
export const ORDER_TYPE_IDS: ReadonlySet<OrderTypeId> = new Set<OrderTypeId>([
  'CMS_485',
  'FREQUENCY_DISCONTINUE_ORDER',
  'FREQUENCY_ORDER',
  'PRN_ORDER',
  'CMS_486',
  'PHYSICIAN_ORDER',
  'POST_HOSPITAL_ORDER',
  'TP_1',
  'TP_2',
  'SCIC_ORDER',
  'F2F_ENCOUNTER',
  'MEDICAID_POC',
  'PLAN_OF_CARE_ADDENDUM',
  'MEDICATION_ORDER',
]);

export const isOrderType = (typeId: TaskTypeId): typeId is OrderTypeId =>
  ORDER_TYPE_IDS.has(typeId as OrderTypeId);

/**
 * Order type IDs we keep in the enum for legacy data but hide from creation
 * pickers (Add Task, intake document tagging, etc.). CMS 486 will not be
 * supported (per ROG-2154); SCIC support is tracked separately in
 * ROG-2160 / ROG-2209.
 */
export const HIDDEN_ORDER_TYPE_IDS: ReadonlySet<OrderTypeId> =
  new Set<OrderTypeId>(['CMS_486', 'SCIC_ORDER']);

export const isHiddenOrderType = (typeId: TaskTypeId): boolean =>
  HIDDEN_ORDER_TYPE_IDS.has(typeId as OrderTypeId);

export function isCreatableVisitTypeId(typeId: TaskTypeId): boolean {
  if (isOrderType(typeId)) {
    return !isHiddenOrderType(typeId);
  }
  if (visitTypeLegacyById && typeId in visitTypeLegacyById) {
    return !visitTypeLegacyById[typeId];
  }
  return false;
}
